/**
 * `*ngModuleOn="'notifications'"` — render the host only while a module is ON
 * for the current user (design §3b, the in-page surfaces that are not routes:
 * the header bell, the mobile-nav badge, the org-board "invite" button…).
 *
 * Re-evaluates on every switch snapshot and on every user/permission update,
 * so a flip in the admin tool takes effect without a reload.
 */
import { Directive, Input, OnDestroy, OnInit, TemplateRef, ViewContainerRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { ModuleKey } from '@newgenus/common';
import { ModuleSwitchService } from '../services/module-switch.service';
import { SecurityService } from '../services/security.service';

// Deliberately "ng"-prefixed (not "newgenus"): this is the documented public API for the
// dark-deploy module switchboard's structural directive, `*ngModuleOn="'key'"`, matching
// Angular's own *ngIf/*ngFor idiom on purpose (CLAUDE.md, "Dark-deploy module switchboard").
// eslint-disable-next-line @angular-eslint/directive-selector
@Directive({ selector: '[ngModuleOn]', standalone: true })
export class ModuleOnDirective implements OnInit, OnDestroy {
    @Input('ngModuleOn') module!: ModuleKey;

    private sub?: Subscription;
    private shown = false;

    constructor(
        private tpl: TemplateRef<unknown>,
        private vcr: ViewContainerRef,
        private moduleSwitch: ModuleSwitchService,
        private security: SecurityService
    ) {}

    ngOnInit(): void {
        // Re-render on every switch snapshot AND on every user/permission update (uid emissions);
        // both streams may be plain Subjects, so evaluate once up front as well.
        this.sub = new Subscription();
        this.sub.add(this.moduleSwitch.isOn$(this.module, {}).subscribe(() => this.render()));
        const user$ = this.security.permissionBehaviorSub$;
        if (user$) this.sub.add(user$.subscribe(() => this.render()));
        this.render();
    }

    ngOnDestroy(): void {
        this.sub?.unsubscribe();
    }

    private render(): void {
        const user = this.security.user;
        const orgId = user?.organizations?.selected;
        const on = this.moduleSwitch.isOn(this.module, {
            orgId,
            userFeatures:
                orgId && user?.organizations
                    ? ((user.organizations[orgId]?.features as string[] | undefined) ?? [])
                    : [],
            isDeveloper: !!user?.isDeveloper,
        });
        if (on && !this.shown) {
            this.vcr.createEmbeddedView(this.tpl);
            this.shown = true;
        } else if (!on && this.shown) {
            this.vcr.clear();
            this.shown = false;
        }
    }
}
